import { format } from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { Td, Th, Tr, TableVirtual, Thead, TableCellSearchHighlight, TableCellText } from '@/common/ui/Table'
import { VirtualCardGrid } from '@/common/ui/VirtualCardGrid'
import { Card } from '@/common/ui/Card'
import { InputSearch, ListFilter, ListSort, type ListFilterSelectField } from '@/common/ui/listToolbar'
import { useBrands } from '@/app/entities/brands/model'
import { useSellers } from '@/common/entities/sellers'
import { useDeleteProduct, useProductsInfinite } from '../model'
import {
    useState,
    useCallback,
    useEffect,
    useRef,
    useId,
    useMemo,
    type ChangeEvent,
    type MouseEvent as ReactMouseEvent,
} from 'react'
import { useMeasure } from '@/common/hooks/useMeasure'
import { defaultUrlListQueryParams, useUrlListFilters } from '@/common/hooks/useUrlListFilters'
import type { MarketplaceType, ProductSource } from '@/common/api/client/types.gen'
import { useTableRowSelection } from '@/common/hooks/useTableRowSelection'
import { Icon } from '@/common/ui/Icon/Icon'
import { Score } from '@/app/ui/Score'
import { Button } from '@/common/ui/Form/Button'
import { CompetitionForm } from '@/app/entities/competition/ui/CompetitionForm'
import { ProductAutoPipelineChooser } from './ProductAutoPipelineChooser'
import { useCurrentOrg } from '@/app/entities/org/model'
import { Checkbox } from '@/common/ui/Form/Checkbox'
import { getGenerationRoReExportExcelApiV1GenerationsOrgIdGenerationsExportExcelPost } from '@/common/api/client/sdk.gen'
import { Modal } from '@/common/ui/Modal'
import { Portal } from '@/common/ui/Portal/Portal'
import { ProductCreateForm } from '@/app/entities/product-deprecated/ui'
import { ImportTableForm } from '@/app/entities/import/ui/ImportTableForm'
import { ChoiceGroup } from '@/common/ui/ChoiceGroup'
import { PhotoPreview } from '@/common/ui/PhotoPreview'
import { Menu, MenuItem } from '@/common/ui/Menu'
import '@/common/ui/Dropdown/Dropdown.css'
import { Tag, type TagColor } from '@/common/ui/Tag'
import { cn } from '@/common/lib/cn/cn'
import { NonIdealState } from '@/common/ui/NonIdealState'
import { resolveRelativeAssetUrl } from '@/common/lib/assetsUrl'
import { analyzeProductContentApiV1GenerationsOrgIdValidateProductProductProductIdPost } from '@/common/api/client/sdk.gen'
import { useMe } from '@/common/entities/me/model/me'

type ProductAddStep = 'chooser' | 'new' | 'marketplace' | 'competitor'

type ProductAddOption = {
    key: ProductAddStep
    title: string
    description: string
    imageSrc: string
}

const ROW_HEIGHT = 72
const CARD_MIN_WIDTH = 220
const CARD_ROW_HEIGHT = 300
const CARD_GAP = 12
const CARD_ROW_GAP = 12
const PRODUCTS_CATALOG_VIEW_MODE_KEY = 'products-catalog-view-mode'

type ProductCatalogViewMode = 'table' | 'cards'

const readCatalogViewMode = (): ProductCatalogViewMode => {
    if (typeof window === 'undefined') return 'table'
    const stored = window.localStorage.getItem(PRODUCTS_CATALOG_VIEW_MODE_KEY)
    return stored === 'cards' ? 'cards' : 'table'
}

/** Фиксированные ширины первых колонок: чекбокс, маркетплейс, превью. */
const PRODUCT_TABLE_COL_CHECKBOX_CLASS = 'w-13 min-w-13 max-w-13 box-border'
const PRODUCT_TABLE_COL_MP_CLASS = 'w-10 min-w-10 max-w-10 box-border'
const PRODUCT_TABLE_COL_PHOTO_CLASS = 'w-14 min-w-14 max-w-14 box-border'

const PRODUCT_LIST_MARKETPLACE_ALLOWLIST: readonly MarketplaceType[] = [
    'wildberries',
    'ozon',
    'yandex_market',
    'avito',
    'megamarket',
]

const PRODUCT_LIST_ORDER_BY_ALLOWLIST = ['created_at', 'updated_at', 'generation_score'] as const

const PRODUCT_LIST_SOURCE_ALLOWLIST = [
    'created',
    'organization',
    'competition',
] as const satisfies readonly ProductSource[]

const PRODUCT_SOURCE_LABELS: Record<ProductSource, string> = {
    competition: 'конкурент',
    organization: 'импорт',
    created: 'локальный',
}

const PRODUCT_SOURCE_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'Любой' },
    ...PRODUCT_LIST_SOURCE_ALLOWLIST.map(src => ({
        value: src,
        label: PRODUCT_SOURCE_LABELS[src],
    })),
]

const PRODUCT_SCORE_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'Любая' },
    ...[1, 2, 3, 4, 5].map(n => ({ value: String(n), label: String(n) })),
]

const PRODUCT_LIST_MARKETPLACE_FILTER_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: 'Все площадки' },
    { value: 'wildberries', label: 'Wildberries' },
    { value: 'ozon', label: 'Ozon' },
    { value: 'yandex_market', label: 'Яндекс Маркет' },
    { value: 'avito', label: 'Авито' },
    { value: 'megamarket', label: 'Мегамаркет' },
]

type ProductListOrderByKey = (typeof PRODUCT_LIST_ORDER_BY_ALLOWLIST)[number]

type ProductListFilterDraft = {
    marketplace: string
    score: string
    source: string
    brand: string
    seller: string
}

const PRODUCT_LIST_FILTER_FIELDS: ListFilterSelectField<keyof ProductListFilterDraft>[] = [
    {
        key: 'marketplace',
        label: 'Маркетплейс',
        placeholder: 'Маркетплейс',
        ariaLabel: 'Маркетплейс',
        options: PRODUCT_LIST_MARKETPLACE_FILTER_OPTIONS,
    },
    {
        key: 'score',
        label: 'Оценка по результатам анализа',
        placeholder: 'Оценка',
        ariaLabel: 'Оценка по результатам анализа',
        options: PRODUCT_SCORE_FILTER_OPTIONS,
    },
    {
        key: 'source',
        label: 'Источник',
        placeholder: 'Источник',
        ariaLabel: 'Источник товара',
        options: PRODUCT_SOURCE_FILTER_OPTIONS,
    },
]

const PRODUCT_SORT_FIELD_OPTIONS: { key: ProductListOrderByKey; label: string }[] = [
    { key: 'updated_at', label: 'Дата обновления' },
    { key: 'created_at', label: 'Дата создания' },
    { key: 'generation_score', label: 'Оценка' },
]

const PRODUCT_LIST_CHOICE_TABS = ['Все', 'Локальные', 'Импортированные', 'Конкуренты'] as const

const PRODUCT_VIEW_MODE_ITEMS = [{ iconName: 'task-square' as const }, { iconName: 'gallery' as const }]

const PRODUCT_LIST_EMPTY_STATE_TITLE = 'Каталог пуст'

const PRODUCT_LIST_EMPTY_STATE_DESCRIPTION = (
    <div className='flex w-full flex-col gap-3 text-center'>
        <p className='leading-[1.4]'>
            Создайте карточку своего товара, импортируйте позиции с маркетплейсов или добавьте товары конкурентов — так
            вы получите рекомендации по улучшению, сможете генерировать новые изображения и тексты.
        </p>
    </div>
)

const productSourceTagColor = (source: ProductSource): TagColor => {
    if (source === 'competition') return 'yellow'
    if (source === 'organization') return 'blue'
    return 'green'
}

const ROW_MENU_WIDTH = 180
const ROW_MENU_GAP = 6
const ROW_MENU_EST_HEIGHT = 160
const CONFIRM_DELETE_PRODUCT_MODAL_ID = 'confirm-delete-product-modal'

const computeRowMenuStyle = (rect: DOMRect) => {
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const spaceBelow = viewportHeight - rect.bottom - ROW_MENU_GAP
    const spaceAbove = rect.top - ROW_MENU_GAP
    const showAbove = spaceBelow < ROW_MENU_EST_HEIGHT && spaceAbove > spaceBelow

    let left = rect.right - ROW_MENU_WIDTH
    if (left < ROW_MENU_GAP) left = ROW_MENU_GAP
    if (left + ROW_MENU_WIDTH > viewportWidth - ROW_MENU_GAP) {
        left = Math.max(ROW_MENU_GAP, viewportWidth - ROW_MENU_WIDTH - ROW_MENU_GAP)
    }

    if (showAbove) {
        return {
            bottom: viewportHeight - rect.top + ROW_MENU_GAP,
            left,
            width: ROW_MENU_WIDTH,
        }
    }
    return {
        top: rect.bottom + ROW_MENU_GAP,
        left,
        width: ROW_MENU_WIDTH,
    }
}

/** Не дефолт API: `updated_at` + `desc` (в URL часто без query). */
const isCustomProductSort = (orderBy: string, direction: 'asc' | 'desc') =>
    orderBy !== 'updated_at' || direction === 'asc'

interface ProductListProps {
    source?: string
}

export const ProductList = ({ source }: ProductListProps) => {
    const [choiceIndex, setChoiceIndex] = useState(source === 'generations' ? 3 : 0)
    const [, setSearchParams] = useSearchParams()
    const { data: currentOrg } = useCurrentOrg()
    const urlFilters = useUrlListFilters({
        queryParams: {
            ...defaultUrlListQueryParams,
            slug: 'marketplace',
            score: 'score',
            source: 'source',
            brand: 'brand',
            seller: 'seller',
        },
        slugAllowlist: PRODUCT_LIST_MARKETPLACE_ALLOWLIST,
        sourceFilterAllowlist: PRODUCT_LIST_SOURCE_ALLOWLIST,
        orderByAllowlist: PRODUCT_LIST_ORDER_BY_ALLOWLIST,
        defaultOrderBy: 'updated_at',
    })
    const setSearchDraft = urlFilters.setSearchDraft

    const {
        allItems,
        totalCount,
        isLoading,
        isFetching,
        error,
        isFetched,
        scrollToIndex,
        onVisibleIndicesChange,
        tabFromUrl,
        handleTabChange: handleTabChangeFromHook,
        fetchAllSelectableIds,
    } = useProductsInfinite(source, {
        q: urlFilters.q,
        marketplace: urlFilters.slug,
        order_by: urlFilters.orderBy as 'created_at' | 'updated_at' | 'generation_score',
        direction: urlFilters.direction,
        score: urlFilters.score,
        sourceFilter: urlFilters.filterSource ? (urlFilters.filterSource as ProductSource) : undefined,
        brand: urlFilters.filterBrand,
        seller: urlFilters.filterSeller,
    })

    // Предзагрузка брендов: опции фильтра готовы в кэше до открытия попапа
    const { data: brandsPage } = useBrands()
    const { data: sellersPage } = useSellers(currentOrg?.id)

    const brandFilterOptions = useMemo(
        () => [
            { value: '', label: 'Все бренды' },
            ...(brandsPage?.items ?? []).map(brand => ({ value: brand.id, label: brand.name })),
        ],
        [brandsPage]
    )

    const sellerFilterOptions = useMemo(
        () => [
            { value: '', label: 'Все продавцы' },
            ...(sellersPage?.items ?? []).map(seller => ({
                value: seller.id,
                label: seller.name ?? seller.id,
            })),
        ],
        [sellersPage]
    )

    const productListFilterFields = useMemo(() => {
        const baseFields =
            choiceIndex === 0 ? PRODUCT_LIST_FILTER_FIELDS : PRODUCT_LIST_FILTER_FIELDS.filter(f => f.key !== 'source')
        const brandField: ListFilterSelectField<keyof ProductListFilterDraft> = {
            key: 'brand',
            label: 'Бренд',
            placeholder: 'Бренд',
            ariaLabel: 'Бренд товара',
            options: brandFilterOptions,
        }
        const sellerField: ListFilterSelectField<keyof ProductListFilterDraft> = {
            key: 'seller',
            label: 'Продавец',
            placeholder: 'Продавец',
            ariaLabel: 'Продавец товара',
            options: sellerFilterOptions,
        }
        return [...baseFields, brandField, sellerField]
    }, [choiceIndex, brandFilterOptions, sellerFilterOptions])

    useEffect(() => {
        setChoiceIndex(tabFromUrl)
    }, [tabFromUrl])

    useEffect(() => {
        if (tabFromUrl === 0) return
        setSearchParams(
            prev => {
                if (!prev.get('source')) return prev
                const next = new URLSearchParams(prev)
                next.delete('source')
                return next
            },
            { replace: true }
        )
    }, [tabFromUrl, setSearchParams])

    const handleTabChange = useCallback(
        (index: number) => {
            setChoiceIndex(index)
            handleTabChangeFromHook(index)
        },
        [handleTabChangeFromHook]
    )

    const handleSortFieldClick = useCallback(
        (field: ProductListOrderByKey) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev)
                    if (urlFilters.orderBy === field) {
                        const newDir = urlFilters.direction === 'desc' ? 'asc' : 'desc'
                        if (newDir === 'desc') next.delete('direction')
                        else next.set('direction', 'asc')
                    } else {
                        if (field === 'updated_at') next.delete('order_by')
                        else next.set('order_by', field)
                        next.delete('direction')
                    }
                    next.delete('scroll')
                    return next
                },
                { replace: true }
            )
        },
        [setSearchParams, urlFilters.orderBy, urlFilters.direction]
    )

    const handleSortFieldSelectChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            handleSortFieldClick(e.target.value as ProductListOrderByKey)
        },
        [handleSortFieldClick]
    )

    const [filterModalDraft, setFilterModalDraft] = useState<ProductListFilterDraft>({
        marketplace: '',
        score: '',
        source: '',
        brand: '',
        seller: '',
    })

    const hasAdvancedFiltersActive =
        Boolean(urlFilters.slug) ||
        urlFilters.score != null ||
        Boolean(urlFilters.filterBrand) ||
        Boolean(urlFilters.filterSeller) ||
        (choiceIndex === 0 && Boolean(urlFilters.filterSource))

    const hasAnyListFilter = hasAdvancedFiltersActive || Boolean(urlFilters.q?.trim())

    const handleListFilterBeforeOpen = useCallback(() => {
        setFilterModalDraft({
            marketplace: urlFilters.slugSelectValue,
            score: urlFilters.scoreSelectValue,
            source: urlFilters.sourceSelectValue,
            brand: urlFilters.brandSelectValue,
            seller: urlFilters.sellerSelectValue,
        })
    }, [
        urlFilters.slugSelectValue,
        urlFilters.scoreSelectValue,
        urlFilters.sourceSelectValue,
        urlFilters.brandSelectValue,
        urlFilters.sellerSelectValue,
    ])

    const handleApplyFiltersPopover = useCallback(() => {
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                const { marketplace, score, source, brand, seller } = filterModalDraft
                if (marketplace) next.set('marketplace', marketplace)
                else next.delete('marketplace')
                if (score) next.set('score', score)
                else next.delete('score')
                if (brand) next.set('brand', brand)
                else next.delete('brand')
                if (seller) next.set('seller', seller)
                else next.delete('seller')
                if (choiceIndex === 0) {
                    if (source) next.set('source', source)
                    else next.delete('source')
                } else {
                    next.delete('source')
                }
                next.delete('scroll')
                return next
            },
            { replace: true }
        )
    }, [filterModalDraft, choiceIndex, setSearchParams])

    const handleClearFiltersPopover = useCallback(() => {
        setFilterModalDraft({ marketplace: '', score: '', source: '', brand: '', seller: '' })
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                next.delete('marketplace')
                next.delete('score')
                next.delete('source')
                next.delete('brand')
                next.delete('seller')
                next.delete('scroll')
                return next
            },
            { replace: true }
        )
    }, [setSearchParams])

    const handleClearAllFiltersForEmpty = useCallback(() => {
        setFilterModalDraft({ marketplace: '', score: '', source: '', brand: '', seller: '' })
        setSearchDraft('')
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev)
                next.delete('q')
                next.delete('marketplace')
                next.delete('score')
                next.delete('source')
                next.delete('brand')
                next.delete('seller')
                next.delete('scroll')
                return next
            },
            { replace: true }
        )
    }, [setSearchDraft, setSearchParams])

    const canFetchProducts = Boolean(currentOrg?.id)

    /** После первого успешного ответа по орг — не перекрываем экран при смене вкладки/перезапросе. */
    const [catalogListReadyOnce, setCatalogListReadyOnce] = useState(false)
    useEffect(() => {
        setCatalogListReadyOnce(false)
    }, [currentOrg?.id])
    useEffect(() => {
        if (canFetchProducts && isFetched && !error) {
            setCatalogListReadyOnce(true)
        }
    }, [canFetchProducts, isFetched, error])

    const { me } = useMe()
    const [isExporting, setIsExporting] = useState(false)
    const [isBatchRestarting, setIsBatchRestarting] = useState(false)
    const [exportingProductId, setExportingProductId] = useState<string | null>(null)
    const [productToDelete, setProductToDelete] = useState<string | null>(null)
    const { mutate: deleteProduct, isPending: isDeletingProduct } = useDeleteProduct()
    const rowMenuListId = useId()
    const [rowMenuProductId, setRowMenuProductId] = useState<string | null>(null)
    const [rowMenuStyle, setRowMenuStyle] = useState<{
        top?: number
        bottom?: number
        left: number
        width: number
    } | null>(null)
    const rowMenuPortalRef = useRef<HTMLDivElement>(null)
    const [productAddStep, setProductAddStep] = useState<ProductAddStep>('chooser')
    const [viewMode, setViewMode] = useState<ProductCatalogViewMode>(() => readCatalogViewMode())
    const isMarketplaceRegistration = me?.registration_type === 'marketplace'

    const handleViewModeChange = useCallback((mode: ProductCatalogViewMode) => {
        setViewMode(mode)
        window.localStorage.setItem(PRODUCTS_CATALOG_VIEW_MODE_KEY, mode)
    }, [])

    const handleViewModeChoiceChange = useCallback(
        (index: number) => {
            handleViewModeChange(index === 0 ? 'table' : 'cards')
        },
        [handleViewModeChange]
    )

    const { selectedIds, handleCheckboxChange, handleSelectAll, allSelected, someSelected, isSelectingAll } =
        useTableRowSelection({
            fetchAllIds: fetchAllSelectableIds,
            resetKey: `${choiceIndex}-${urlFilters.filterKey}`,
        })

    const productAddOptions: ProductAddOption[] = [
        {
            key: 'new',
            title: 'Новый товар',
            description: 'Если у вас ещё нет магазина, создайте карточку своего первого товара прямо сейчас.',
            imageSrc: '/product-add/new-item.png',
        },
        {
            key: 'marketplace',
            title: 'Товар из маркетплейса',
            description:
                'Загружайте товары из маркетплейсов для получения рекомендаций по улучшению, генерации новых картинок и текста.',
            imageSrc: '/product-add/marketplace.png',
        },
        ...(!isMarketplaceRegistration
            ? [
                  {
                      key: 'competitor',
                      title: 'Товар по ссылке',
                      description: 'Вставьте ссылку на товар.',
                      imageSrc: '/product-add/competitor.png',
                  } satisfies ProductAddOption,
              ]
            : []),
    ]

    const handleOpenAddModal = () => {
        setProductAddStep('chooser')
    }

    const handleBackToChooser = () => {
        setProductAddStep('chooser')
    }

    const downloadReportBlob = useCallback((blob: Blob) => {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `report_${new Date().toISOString().split('T')[0]}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }, [])

    const handleExport = useCallback(async () => {
        if (!currentOrg?.id || selectedIds.size === 0) return

        try {
            setIsExporting(true)
            const productIds = Array.from(selectedIds)
            const blob = (
                await getGenerationRoReExportExcelApiV1GenerationsOrgIdGenerationsExportExcelPost({
                    path: { org_id: currentOrg.id },
                    body: { product_ids: productIds },
                    responseType: 'blob',
                })
            ).data as Blob
            downloadReportBlob(blob)
        } catch (err) {
            console.error('Ошибка при экспорте в Excel:', err)
        } finally {
            setIsExporting(false)
        }
    }, [currentOrg?.id, selectedIds, downloadReportBlob])

    const handleBatchRestart = useCallback(async () => {
        if (!currentOrg?.id || selectedIds.size === 0 || !me?.is_admin) return

        try {
            setIsBatchRestarting(true)
            const productIds = Array.from(selectedIds)
            await Promise.allSettled(
                productIds.map(productId =>
                    analyzeProductContentApiV1GenerationsOrgIdValidateProductProductProductIdPost({
                        path: { org_id: currentOrg.id, product_id: productId },
                    })
                )
            )
        } catch (err) {
            console.error('Ошибка при массовом перезапуске анализа:', err)
        } finally {
            setIsBatchRestarting(false)
        }
    }, [currentOrg?.id, selectedIds, me?.is_admin])

    const handleExportProduct = useCallback(
        async (productId: string) => {
            if (!currentOrg?.id) return

            try {
                setExportingProductId(productId)
                const blob = (
                    await getGenerationRoReExportExcelApiV1GenerationsOrgIdGenerationsExportExcelPost({
                        path: { org_id: currentOrg.id },
                        body: { product_ids: [productId] },
                        responseType: 'blob',
                    })
                ).data as Blob
                downloadReportBlob(blob)
            } catch (err) {
                console.error('Ошибка при экспорте в Excel:', err)
            } finally {
                setExportingProductId(null)
            }
        },
        [currentOrg?.id, downloadReportBlob]
    )

    const closeRowMenu = useCallback(() => {
        setRowMenuProductId(null)
        setRowMenuStyle(null)
    }, [])

    const handleRowKebabClick = useCallback(
        (e: ReactMouseEvent<HTMLButtonElement>, productId: string) => {
            e.stopPropagation()
            if (rowMenuProductId === productId) {
                closeRowMenu()
                return
            }
            setRowMenuProductId(productId)
            setRowMenuStyle(computeRowMenuStyle(e.currentTarget.getBoundingClientRect()))
        },
        [rowMenuProductId, closeRowMenu]
    )

    const rowMenuProduct = useMemo(
        () => (rowMenuProductId ? allItems.find(p => p?.id === rowMenuProductId) : undefined),
        [rowMenuProductId, allItems]
    )

    const handleRowMenuDownload = useCallback(() => {
        if (!rowMenuProductId) return
        const id = rowMenuProductId
        void handleExportProduct(id)
        closeRowMenu()
    }, [rowMenuProductId, handleExportProduct, closeRowMenu])

    const closeConfirmDeleteProductModal = useCallback(() => {
        setProductToDelete(null)
        const checkbox = document.getElementById(CONFIRM_DELETE_PRODUCT_MODAL_ID) as HTMLInputElement | null
        if (checkbox) checkbox.checked = false
    }, [])

    const handleRowMenuDeleteClick = useCallback(() => {
        if (!rowMenuProductId) return
        setProductToDelete(rowMenuProductId)
        closeRowMenu()
        const checkbox = document.getElementById(CONFIRM_DELETE_PRODUCT_MODAL_ID) as HTMLInputElement | null
        if (checkbox) checkbox.checked = true
    }, [rowMenuProductId, closeRowMenu])

    const handleConfirmDeleteProduct = useCallback(() => {
        if (!productToDelete) return
        deleteProduct(productToDelete, {
            onSettled: () => {
                closeConfirmDeleteProductModal()
            },
        })
    }, [productToDelete, deleteProduct, closeConfirmDeleteProductModal])

    useEffect(() => {
        if (!rowMenuProductId) return
        if (!allItems.some(p => p?.id === rowMenuProductId)) closeRowMenu()
    }, [allItems, rowMenuProductId, closeRowMenu])

    useEffect(() => {
        if (!rowMenuProductId) return

        const update = () => {
            const el = document.querySelector<HTMLElement>(`[data-row-menu-trigger="${rowMenuProductId}"]`)
            if (!el) {
                closeRowMenu()
                return
            }
            setRowMenuStyle(computeRowMenuStyle(el.getBoundingClientRect()))
        }

        window.addEventListener('scroll', update, true)
        window.addEventListener('resize', update)
        return () => {
            window.removeEventListener('scroll', update, true)
            window.removeEventListener('resize', update)
        }
    }, [rowMenuProductId, closeRowMenu])

    useEffect(() => {
        if (!rowMenuProductId) return

        const handlePointerDown = (event: globalThis.MouseEvent) => {
            const target = event.target as Node
            if (rowMenuPortalRef.current?.contains(target)) return
            const trigger = document.querySelector(`[data-row-menu-trigger="${rowMenuProductId}"]`)
            if (trigger?.contains(target)) return
            closeRowMenu()
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeRowMenu()
        }

        document.addEventListener('mousedown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [rowMenuProductId, closeRowMenu])

    const [tableContainerRef, tableDimensions] = useMeasure()

    const renderProductRow = useCallback(
        (index: number) => {
            const product = allItems[index]
            if (!product) {
                return (
                    <Tr aria-hidden>
                        <Td colSpan={9} className='text-center text-base-content/50'>
                            Загрузка...
                        </Td>
                    </Tr>
                )
            }
            return (
                <Tr
                    to={`/app/products/${product.id}`}
                    data-selected={selectedIds.has(product.id) ? true : undefined}
                    className='group hover:bg-[rgba(190,150,255,0.07)] transition-colors'
                    aria-label={`Открыть товар ${product.name}`}
                >
                    <Td className={PRODUCT_TABLE_COL_CHECKBOX_CLASS} onClick={e => e.stopPropagation()}>
                        <Checkbox
                            disabled={product.generation_score === null}
                            checked={selectedIds.has(product.id)}
                            onChange={e => handleCheckboxChange(product.id, e.target.checked)}
                        />
                    </Td>
                    <Td className={cn('text-center', PRODUCT_TABLE_COL_MP_CLASS)}>
                        {product.marketplace === 'wildberries' ? (
                            <span title='Wildberries' className='block'>
                                <Icon name='wb' />
                            </span>
                        ) : (
                            <span title='Ozon' className='block'>
                                <Icon name='ozon' />
                            </span>
                        )}
                    </Td>
                    <Td className={PRODUCT_TABLE_COL_PHOTO_CLASS}>
                        <PhotoPreview src={resolveRelativeAssetUrl(product.preview)} alt={product.name} />
                    </Td>
                    <Td>
                        <NavLink to={`/app/products/${product.id}/analytics`}>
                            <TableCellSearchHighlight
                                text={product.name}
                                searchHighlight={urlFilters.searchHighlight}
                                lines={1}
                            />
                        </NavLink>
                    </Td>
                    <Td className='text-center'>
                        {product.generation_score && product.max_generation_score ? (
                            <Score
                                color
                                score={Number(product.generation_score)}
                                maxScore={Number(product.max_generation_score)}
                            />
                        ) : (
                            '—'
                        )}
                    </Td>
                    <Td>
                        <TableCellText lines={1}>{product.category}</TableCellText>
                    </Td>
                    {/* <Td>{product.vendor_id}</Td>
                    <Td>{product.marketplace_id}</Td> */}
                    <Td className='whitespace-nowrap'>{format(product.updated_at as string, 'dd.MM.yyyy HH:mm')}</Td>
                    {/* <Td>{format(product.created_at as string, 'dd.MM.yyyy HH:mm')}</Td> */}
                    <Td className='text-center'>
                        <Tag color={productSourceTagColor(product.source)}>{PRODUCT_SOURCE_LABELS[product.source]}</Tag>
                    </Td>
                    <Td className='w-px min-w-[44px]' onClick={e => e.stopPropagation()}>
                        <div className='flex justify-end opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100'>
                            <Button
                                type='button'
                                variant='ghost'
                                size='lg'
                                emphasis='accent-secondary'
                                aria-label='Действия с товаром'
                                aria-haspopup='menu'
                                aria-expanded={rowMenuProductId === product.id}
                                aria-controls={rowMenuListId}
                                data-row-menu-trigger={product.id}
                                onClick={(e: ReactMouseEvent<HTMLButtonElement>) => handleRowKebabClick(e, product.id)}
                            >
                                <Icon name='kebab' size='lg' />
                            </Button>
                        </div>
                    </Td>
                </Tr>
            )
        },
        [
            allItems,
            selectedIds,
            handleCheckboxChange,
            handleRowKebabClick,
            rowMenuProductId,
            rowMenuListId,
            urlFilters.searchHighlight,
        ]
    )

    const renderProductCard = useCallback(
        (index: number) => {
            const product = allItems[index]
            if (!product) {
                return (
                    <Card key={`loading-${index}`} type='product' className='h-full opacity-60'>
                        <div className='flex h-full items-center justify-center text-base-content/50'>Загрузка...</div>
                    </Card>
                )
            }

            const isSelected = selectedIds.has(product.id)

            return (
                <Card
                    key={product.id}
                    type='product'
                    to={`/app/products/${product.id}`}
                    className={cn('group relative h-full overflow-hidden transition-colors')}
                    cardBodyClassName='relative flex h-full flex-col !p-0'
                    data-selected={isSelected ? true : undefined}
                    aria-label={`Открыть товар ${product.name}`}
                >
                    <div className='absolute inset-0 bg-[#27272E]'>
                        {product.preview ? (
                            <img
                                src={resolveRelativeAssetUrl(product.preview)}
                                alt=''
                                className='h-full w-full object-cover'
                            />
                        ) : null}
                    </div>

                    <div
                        className={cn(
                            'pointer-events-none absolute inset-0 transition-colors',
                            'group-hover:bg-[rgba(190,150,255,0.07)]',
                            isSelected && 'bg-[rgba(190,150,255,0.1)]'
                        )}
                        aria-hidden='true'
                    />

                    <div
                        className={cn(
                            'relative z-10 flex items-start justify-between gap-2 bg-gradient-to-b from-[rgba(25,15,27,0.96)] via-[rgba(25,15,27,0.82)] to-transparent px-3 pb-7 pt-3 transition-opacity',
                            'pointer-events-none opacity-0',
                            'group-hover:pointer-events-auto group-hover:opacity-100',
                            'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
                            isSelected && 'pointer-events-auto opacity-100',
                            rowMenuProductId === product.id && 'pointer-events-auto opacity-100'
                        )}
                    >
                        <div onClick={e => e.stopPropagation()}>
                            <Checkbox
                                disabled={product.generation_score === null}
                                checked={isSelected}
                                onChange={e => handleCheckboxChange(product.id, e.target.checked)}
                            />
                        </div>
                        <div onClick={e => e.stopPropagation()}>
                            <Button
                                className='mt-[-6px] mr-[-6px]'
                                type='button'
                                variant='ghost'
                                size='lg'
                                emphasis='accent-secondary'
                                aria-label='Действия с товаром'
                                aria-haspopup='menu'
                                aria-expanded={rowMenuProductId === product.id}
                                aria-controls={rowMenuListId}
                                data-row-menu-trigger={product.id}
                                onClick={(e: ReactMouseEvent<HTMLButtonElement>) => handleRowKebabClick(e, product.id)}
                            >
                                <Icon name='kebab' size='lg' />
                            </Button>
                        </div>
                    </div>

                    <div className='relative z-10 mt-auto bg-gradient-to-t from-[rgba(25,15,27,0.96)] via-[rgba(25,15,27,0.82)] to-transparent px-3 pb-3 pt-10'>
                        <NavLink
                            to={`/app/products/${product.id}/analytics`}
                            className='mb-2 block w-full min-w-0'
                            onClick={e => e.stopPropagation()}
                        >
                            <TableCellSearchHighlight
                                text={product.name}
                                searchHighlight={urlFilters.searchHighlight}
                                lines={2}
                                className='text-sm font-medium text-white'
                            />
                        </NavLink>

                        <div className='mb-2 flex flex-wrap items-center gap-2'>
                            {product.marketplace === 'wildberries' ? (
                                <span title='Wildberries'>
                                    <Icon name='wb' />
                                </span>
                            ) : (
                                <span title='Ozon'>
                                    <Icon name='ozon' />
                                </span>
                            )}
                            {product.generation_score && product.max_generation_score ? (
                                <Score
                                    color
                                    score={Number(product.generation_score)}
                                    maxScore={Number(product.max_generation_score)}
                                />
                            ) : (
                                <span className='text-sm text-base-content/50'>—</span>
                            )}
                            <Tag color={productSourceTagColor(product.source)}>
                                {PRODUCT_SOURCE_LABELS[product.source]}
                            </Tag>
                        </div>

                        <div className='flex flex-col gap-1 text-xs text-[var(--gray-2,#aba7af)]'>
                            <TableCellText lines={1} className='text-xs'>
                                {product.category}
                            </TableCellText>
                            <span className='whitespace-nowrap'>
                                {format(product.updated_at as string, 'dd.MM.yyyy HH:mm')}
                            </span>
                        </div>
                    </div>
                </Card>
            )
        },
        [
            allItems,
            selectedIds,
            handleCheckboxChange,
            handleRowKebabClick,
            rowMenuProductId,
            rowMenuListId,
            urlFilters.searchHighlight,
        ]
    )

    const isEmptyList = isFetched && !isLoading && !error && (totalCount ?? 0) === 0
    /** Пустой каталог с иллюстрацией — вкладка «Все», без поиска и без фильтров маркетплейса/оценки/источника. */
    const showNonIdealState = isEmptyList && tabFromUrl === 0 && !hasAnyListFilter
    const tableHeight = Math.max(tableDimensions.height, 200)

    const showBlockingLoader =
        !canFetchProducts || (!catalogListReadyOnce && canFetchProducts && (isLoading || !isFetched))
    const showCatalogToolbar = canFetchProducts && !showBlockingLoader && !showNonIdealState
    const showListLoader = showBlockingLoader
    const showCardsDataLoader = viewMode === 'cards' && showCatalogToolbar && isFetching && !error && !showNonIdealState

    return (
        <div className='flex flex-col flex-1 min-h-0 min-w-0 w-full'>
            {showCatalogToolbar && (
                <div className='flex min-w-0 flex-col gap-[1.25rem] mb-[1rem] shrink-0'>
                    <div className='flex flex-wrap items-center gap-2'>
                        <h3 className='text-lg font-medium'>Каталог товаров</h3>
                        <div className='flex-1' />
                        <div className='flex flex-wrap items-center gap-2'>
                            <Button as='label' size='lg' htmlFor='create-product-modal' onClick={handleOpenAddModal}>
                                <Icon name='plus' size='lg' />
                                Добавить
                            </Button>
                            {!source && someSelected && (
                                <Button
                                    className='ml-2'
                                    variant='outline'
                                    onClick={handleExport}
                                    disabled={isExporting}
                                    loading={isExporting}
                                >
                                    <Icon name='export' size='sm' />
                                    Скачать отчет
                                </Button>
                            )}
                            {!source && someSelected && me?.is_admin && (
                                <Button
                                    variant='outline'
                                    onClick={handleBatchRestart}
                                    disabled={isBatchRestarting}
                                    loading={isBatchRestarting}
                                >
                                    <Icon name='refresh' size='sm' />
                                    Групповой перезапуск
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className='flex min-w-0 w-full max-w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex w-full min-w-0 max-w-full items-center gap-3 lg:min-w-0 lg:max-w-full lg:flex-1'>
                            <div
                                className='min-w-0 w-0 flex-1 overflow-hidden'
                                title='Переключение вкладок списка товаров'
                            >
                                <ChoiceGroup
                                    items={[...PRODUCT_LIST_CHOICE_TABS]}
                                    activeIndex={choiceIndex}
                                    onChange={handleTabChange}
                                />
                            </div>

                            <div className='shrink-0' title='Переключение режима отображения товаров'>
                                <ChoiceGroup
                                    className='shrink-0'
                                    items={PRODUCT_VIEW_MODE_ITEMS}
                                    activeIndex={viewMode === 'table' ? 0 : 1}
                                    onChange={handleViewModeChoiceChange}
                                />
                            </div>
                        </div>

                        <div className='flex min-w-0 w-full items-center gap-3 lg:flex-1'>
                            {viewMode === 'cards' && (
                                <label className='flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap leading-none ml-[12px] lg:ml-0'>
                                    <Checkbox
                                        checked={allSelected}
                                        disabled={isSelectingAll}
                                        onChange={e => void handleSelectAll(e.target.checked)}
                                    />
                                    <span className='text-sm text-white'>Выбрать все</span>
                                </label>
                            )}
                            <div className='flex w-full min-w-0 justify-end flex-1 flex-nowrap items-center gap-4'>
                                <InputSearch
                                    name='products-search'
                                    value={urlFilters.searchDraft}
                                    onChange={e => urlFilters.setSearchDraft(e.target.value)}
                                    appliedSearch={urlFilters.q}
                                    className='flex-1 max-w-[300px]'
                                    placeholder='Поиск по названию'
                                    aria-label='Поиск по названию товара'
                                    title='Поиск по названию товара'
                                />
                                <div title='Фильтры списка товаров'>
                                    <ListFilter
                                        active={hasAdvancedFiltersActive}
                                        onBeforeOpen={handleListFilterBeforeOpen}
                                        onApply={handleApplyFiltersPopover}
                                        onClear={handleClearFiltersPopover}
                                        aria-label='Фильтры списка товаров'
                                        width={350}
                                        maxHeight={420}
                                        filterSelectFields={productListFilterFields}
                                        filterValues={filterModalDraft}
                                        onFilterValueChange={(key, value) =>
                                            setFilterModalDraft(d => ({ ...d, [key]: value }))
                                        }
                                    />
                                </div>
                                <div title='Сортировка списка товаров'>
                                    <ListSort
                                        options={PRODUCT_SORT_FIELD_OPTIONS}
                                        value={urlFilters.orderBy}
                                        direction={urlFilters.direction}
                                        onChange={handleSortFieldSelectChange}
                                        highlightActive={isCustomProductSort(urlFilters.orderBy, urlFilters.direction)}
                                        aria-label='Сортировка списка товаров: поле и направление'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className='flex-1 relative'>
                <div ref={tableContainerRef} className='absolute inset-0' aria-hidden />
                <div className='absolute inset-0 flex flex-col'>
                    {showListLoader && (
                        <div
                            className='absolute inset-0 flex items-center justify-center'
                            role='status'
                            aria-live='polite'
                            aria-label='Загрузка списка товаров'
                        >
                            <span className='loading loading-spinner loading-lg' />
                        </div>
                    )}
                    {error && (
                        <div className='flex items-center justify-center py-12 text-error'>Ошибка: {error.message}</div>
                    )}
                    {showNonIdealState && (
                        <div className='flex flex-1 min-h-[200px] items-center justify-center'>
                            <NonIdealState
                                title={PRODUCT_LIST_EMPTY_STATE_TITLE}
                                description={PRODUCT_LIST_EMPTY_STATE_DESCRIPTION}
                                image='empty-catalog'
                                actionSlot={
                                    <Button
                                        as='label'
                                        size='xl'
                                        htmlFor='create-product-modal'
                                        onClick={handleOpenAddModal}
                                    >
                                        <Icon name='plus' size='lg' />
                                        Добавить товары
                                    </Button>
                                }
                            />
                        </div>
                    )}
                    {!error && !showNonIdealState && showCatalogToolbar && (
                        <div className='relative flex min-h-0 flex-1 flex-col'>
                            {viewMode === 'table' ? (
                                <TableVirtual
                                    rowCount={totalCount ?? 0}
                                    rowHeight={ROW_HEIGHT}
                                    renderRow={renderProductRow}
                                    height={tableHeight}
                                    striped
                                    thBg
                                    pinRows
                                    onVisibleIndicesChange={onVisibleIndicesChange}
                                    scrollToIndex={scrollToIndex}
                                >
                                    <Thead>
                                        <Tr>
                                            <Th className={PRODUCT_TABLE_COL_CHECKBOX_CLASS}>
                                                <Checkbox
                                                    checked={allSelected}
                                                    disabled={isSelectingAll}
                                                    onChange={e => void handleSelectAll(e.target.checked)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </Th>
                                            <Th className={PRODUCT_TABLE_COL_MP_CLASS}>МП</Th>
                                            <Th className={PRODUCT_TABLE_COL_PHOTO_CLASS}>Фото</Th>
                                            <Th>Название товара</Th>
                                            <Th>Оценка</Th>
                                            <Th>Категория</Th>
                                            <Th>Обновлено</Th>
                                            <Th className='text-center'>Источник</Th>
                                            <Th> </Th>
                                        </Tr>
                                    </Thead>
                                </TableVirtual>
                            ) : (
                                <>
                                    <VirtualCardGrid
                                        itemCount={totalCount ?? 0}
                                        rowHeight={CARD_ROW_HEIGHT}
                                        minItemWidth={CARD_MIN_WIDTH}
                                        gap={CARD_GAP}
                                        rowGap={CARD_ROW_GAP}
                                        renderItem={renderProductCard}
                                        height={tableHeight}
                                        onVisibleIndicesChange={onVisibleIndicesChange}
                                        scrollToIndex={scrollToIndex}
                                    />
                                    {showCardsDataLoader && (
                                        <div
                                            className='pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-[rgba(25,15,27,0.35)]'
                                            role='status'
                                            aria-live='polite'
                                            aria-label='Загрузка товаров'
                                        >
                                            <span className='loading loading-spinner loading-lg' />
                                        </div>
                                    )}
                                </>
                            )}
                            {isEmptyList && (
                                <div
                                    className='pointer-events-none absolute inset-0 flex items-center justify-center px-4 pt-14 text-center'
                                    role='status'
                                    aria-live='polite'
                                >
                                    <div className='pointer-events-auto flex max-w-md flex-col items-center gap-3'>
                                        {hasAnyListFilter ? (
                                            <>
                                                <p className='text-base text-[var(--gray-2,#aba7af)]'>
                                                    Товаров с выбранным фильтром нет.
                                                </p>
                                                <Button
                                                    type='button'
                                                    variant='ghost'
                                                    emphasis='accent'
                                                    size='md'
                                                    onClick={handleClearAllFiltersForEmpty}
                                                    aria-label='Очистить фильтр'
                                                >
                                                    Очистить фильтр
                                                </Button>
                                            </>
                                        ) : (
                                            <p className='text-base text-[var(--gray-2,#aba7af)]'>
                                                {tabFromUrl !== 0 ? 'Товаров в этой категории нет' : 'Товаров нет'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Modal id='create-product-modal' className='full-width'>
                {productAddStep === 'chooser' && (
                    <div className='flex flex-col gap-6'>
                        <h2 className='text-2xl font-medium text-white'>Какой товар вы хотите добавить?</h2>
                        <div className='flex flex-col gap-3'>
                            {productAddOptions.map(option => (
                                <button
                                    key={option.key}
                                    type='button'
                                    className='flex cursor-pointer items-center gap-4 rounded-[24px] bg-[rgba(255,255,255,0.02)] p-4 text-left transition-colors hover:bg-[rgba(255,255,255,0.06)]'
                                    aria-label={option.title}
                                    onClick={() => setProductAddStep(option.key)}
                                >
                                    <img src={option.imageSrc} alt='' className='h-[105px] w-[105px] object-contain' />
                                    <div className='flex flex-1 flex-col gap-5'>
                                        <p className='text-2xl font-medium text-white'>{option.title}</p>
                                        <p className='text-base text-[var(--gray-2,#aba7af)]'>{option.description}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <ProductAutoPipelineChooser modalId='create-product-modal' />
                    </div>
                )}
                {productAddStep === 'new' && <ProductCreateForm onBack={handleBackToChooser} />}
                {productAddStep === 'marketplace' && (
                    <ImportTableForm onBack={handleBackToChooser} modalId='create-product-modal' />
                )}
                {productAddStep === 'competitor' && (
                    <CompetitionForm onBack={handleBackToChooser} modalId='create-product-modal' />
                )}
            </Modal>

            <Modal id={CONFIRM_DELETE_PRODUCT_MODAL_ID}>
                <h3 className='text-2xl font-medium text-white'>Удалить товар?</h3>
                <p className='mt-3 text-base text-[var(--gray-2,#aba7af)]'>
                    Товар будет удалён без возможности восстановления.
                </p>
                <div className='modal-form-actions mt-5'>
                    <Button
                        size='xl'
                        emphasis='negative'
                        variant='filled'
                        disabled={isDeletingProduct}
                        onClick={handleConfirmDeleteProduct}
                    >
                        {isDeletingProduct ? 'Удаление…' : 'Удалить'}
                    </Button>
                    <Button
                        size='lg'
                        emphasis='secondary'
                        variant='ghost'
                        disabled={isDeletingProduct}
                        onClick={closeConfirmDeleteProductModal}
                    >
                        Отмена
                    </Button>
                </div>
            </Modal>

            {rowMenuProductId && rowMenuStyle && (
                <Portal container='body'>
                    <div
                        ref={rowMenuPortalRef}
                        className='fixed z-[950]'
                        style={{
                            ...(rowMenuStyle.top !== undefined && { top: rowMenuStyle.top }),
                            ...(rowMenuStyle.bottom !== undefined && { bottom: rowMenuStyle.bottom }),
                            left: rowMenuStyle.left,
                            width: rowMenuStyle.width,
                        }}
                    >
                        <Menu
                            id={rowMenuListId}
                            role='menu'
                            variant='selectList'
                            className='shadow-sm min-w-[180px] custom-select-menu'
                        >
                            <MenuItem>
                                <NavLink
                                    className='menu-select-row block w-full text-left text-sm text-white'
                                    to={`/app/products/${rowMenuProductId}/analytics`}
                                >
                                    Данные и анализ
                                </NavLink>
                            </MenuItem>
                            <MenuItem>
                                <NavLink
                                    className='menu-select-row block w-full text-left text-sm text-white'
                                    to={`/app/products/${rowMenuProductId}/texts`}
                                >
                                    Тексты и SEO
                                </NavLink>
                            </MenuItem>
                            <MenuItem>
                                <NavLink
                                    className='menu-select-row block w-full text-left text-sm text-white'
                                    to={`/app/products/${rowMenuProductId}/media`}
                                >
                                    Фото и видео
                                </NavLink>
                            </MenuItem>
                            <MenuItem
                                disabled={
                                    rowMenuProduct?.generation_score === null || exportingProductId === rowMenuProductId
                                }
                            >
                                <button
                                    type='button'
                                    className='menu-select-row block w-full text-left text-sm text-white'
                                    disabled={
                                        rowMenuProduct?.generation_score === null ||
                                        exportingProductId === rowMenuProductId
                                    }
                                    onClick={handleRowMenuDownload}
                                >
                                    <Icon name='export' size='sm' />
                                    {exportingProductId === rowMenuProductId ? 'Скачивание…' : 'Скачать отчет'}
                                </button>
                            </MenuItem>
                            <MenuItem disabled={isDeletingProduct}>
                                <button
                                    type='button'
                                    className='menu-select-row flex w-full items-center gap-2 text-left text-sm text-white'
                                    disabled={isDeletingProduct}
                                    aria-label='Удалить товар'
                                    onClick={handleRowMenuDeleteClick}
                                >
                                    <Icon name='trash' size='sm' />
                                    Удалить
                                </button>
                            </MenuItem>
                        </Menu>
                    </div>
                </Portal>
            )}
        </div>
    )
}
